'use client';

/**
 * Single client shell body: path-aware topbar visibility + scroll layout and slots.
 * Renders .app-shell > .shell-body > .main-wrapper > scroll-area > topbar + [aboveMain] + main; composer + bottomBar + footer.
 * Uses SlotsContext (useSlots) for aboveMain, composer, bottomBar, docViewActive. Any module can set slots.
 * @see docs/UX_GUIDELINES.md §6, docs/LAYOUT_AND_STYLING.md
 */

import { DrawerExtraProvider } from '@korvo/core/components/shared/TopBar/DrawerExtraContext'
import { usePathname } from 'next/navigation'
import type { ReactNode } from 'react'
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { PathAwareDrawerContent } from './PathAwareDrawerContent'
import { useSlots } from './SlotsContext'

const MOBILE_BREAKPOINT = 768
const KEYBOARD_THRESHOLD = 150
const KEYBOARD_HEIGHT_MAX = 300

export type ShellBodyProps = {
  topBar: ReactNode
  children: ReactNode
  footer?: ReactNode
  topBarInFlow?: boolean
  /** When true, show topbar on /editor/. Default false. */
  showOnEditorPaths?: boolean
  /** Path prefixes where the topbar is hidden (app provides own nav). */
  hideOnPathPrefixes?: string[]
  /** Path prefixes where the topbar is hidden on desktop but shown on mobile. */
  showOnPathPrefixesOnlyWhenMobile?: string[]
  /** Path prefixes where the topbar is always shown on mobile. Overrides hideOnPathPrefixes on mobile. */
  showOnMobileForPathPrefixes?: string[]
}

export function ShellBody({
  topBar,
  children,
  footer,
  topBarInFlow = true,
  showOnEditorPaths = false,
  hideOnPathPrefixes,
  showOnPathPrefixesOnlyWhenMobile,
  showOnMobileForPathPrefixes,
}: ShellBodyProps) {
  const pathname = usePathname()
  const { slots } = useSlots()
  const [isMobile, setIsMobile] = useState(false)
  const [keyboardOpen, setKeyboardOpen] = useState(false)
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const topbarWrapperRef = useRef<HTMLDivElement>(null)
  const prevScrollAreaHeightRef = useRef<number>(0)

  useLayoutEffect(() => {
    const check = () => setIsMobile(typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null
    if (!vv) return
    const check = () => {
      const vh = vv.height
      const wh = window.innerHeight
      const open = wh - vh > KEYBOARD_THRESHOLD
      setKeyboardOpen(open)
      setKeyboardHeight(open ? Math.min(wh - vh, KEYBOARD_HEIGHT_MAX) : 0)
    }
    check()
    vv.addEventListener('resize', check)
    vv.addEventListener('scroll', check)
    return () => {
      vv.removeEventListener('resize', check)
      vv.removeEventListener('scroll', check)
    }
  }, [])

  useEffect(() => {
    const el = scrollAreaRef.current
    if (!el) return
    prevScrollAreaHeightRef.current = el.clientHeight
    const ro = new ResizeObserver(() => {
      const prevH = prevScrollAreaHeightRef.current
      const newH = el.clientHeight
      if (prevH > 0 && newH !== prevH) {
        if (newH < prevH) {
          const delta = prevH - newH
          el.scrollTop = Math.min(el.scrollTop + delta, Math.max(0, el.scrollHeight - newH))
        } else {
          const delta = newH - prevH
          el.scrollTop = Math.max(0, el.scrollTop - delta)
        }
      }
      prevScrollAreaHeightRef.current = newH
    })
    ro.observe(el)
    return () => {
      ro.disconnect()
      prevScrollAreaHeightRef.current = el.clientHeight
    }
  }, [])

  const pathMatches = (prefixes: string[] | undefined) =>
    prefixes?.some((prefix) => pathname?.startsWith(prefix)) ?? false
  const baseHide =
    ((pathname?.startsWith('/editor/') ?? false) && !showOnEditorPaths) ||
    pathMatches(hideOnPathPrefixes)
  const showOnlyOnMobile = pathMatches(showOnPathPrefixesOnlyWhenMobile)
  const forceShowOnMobile = isMobile && pathMatches(showOnMobileForPathPrefixes)
  const hideTopBar = baseHide && !(showOnlyOnMobile && isMobile) && !forceShowOnMobile
  const effectiveTopBar = hideTopBar ? null : topBar
  const hasTopBar = effectiveTopBar != null

  return (
    <DrawerExtraProvider>
      <PathAwareDrawerContent />
      <div
        className="app-shell"
        id="shell"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          paddingBottom: keyboardOpen ? `${keyboardHeight}px` : 0,
          transition: 'padding-bottom 180ms ease',
          ...(hasTopBar ? {} : { ['--topbar-height' as string]: '0px' }),
        }}
      >
        <div className="shell-body" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row' }}>
          <div className="main-wrapper" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div
              ref={scrollAreaRef}
              id="scrollArea"
              role="main"
              data-testid="scroll-area"
              className={[
                'scroll-area',
                slots.docViewActive && 'doc-view-active',
                keyboardOpen && 'keyboard-open',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: slots.docViewActive ? 'hidden' : 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                overscrollBehaviorY: 'contain',
                scrollPaddingTop: hasTopBar ? '48px' : 0,
              }}
            >
              <div
                id="scrollContent"
                className={slots.docViewActive ? 'scroll-content doc-view-active' : 'scroll-content'}
                style={{
                  position: 'relative',
                  ...(slots.docViewActive ? { display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' } : {}),
                }}
              >
                {hasTopBar && (
                  <div
                    key="topbar"
                    ref={topbarWrapperRef}
                    id="topbar"
                    data-testid="topbar"
                    className={['scroll-aware-topbar', keyboardOpen && 'topbar-keyboard-hide'].filter(Boolean).join(' ')}
                    style={{ position: 'sticky', top: 0, zIndex: 30 }}
                  >
                    {effectiveTopBar}
                  </div>
                )}
                {slots.aboveMain != null ? <Fragment key="aboveMain">{slots.aboveMain}</Fragment> : null}
                <div
                  key="main"
                  id="main"
                  data-testid="main-scroll"
                  className={slots.docViewActive ? 'main-scroll doc-view-active' : 'main-scroll'}
                  style={{
                    minHeight: hasTopBar
                      ? 'var(--shell-main-min-height, calc(100vh - var(--topbar-height, 4rem) - var(--footer-height, 2rem)))'
                      : 'var(--shell-main-min-height-no-topbar, calc(100vh - var(--footer-height, 2rem)))',
                    paddingBottom: slots.docViewActive
                      ? 0
                      : 'calc(var(--footer-height, 2rem) + var(--safe-area-inset-bottom, 0px))',
                    ...(slots.docViewActive
                      ? { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }
                      : {}),
                  }}
                >
                  {children}
                </div>
              </div>
            </div>
            {slots.composer != null ? <Fragment key="composer">{slots.composer}</Fragment> : null}
            {slots.bottomBar != null ? (
              <div key="bottomBar" className={keyboardOpen ? 'bottom-bar keyboard-hidden' : 'bottom-bar'}>
                {slots.bottomBar}
              </div>
            ) : null}
            {slots.bottomBar == null && footer != null ? <Fragment key="footer">{footer}</Fragment> : null}
          </div>
        </div>
      </div>
    </DrawerExtraProvider>
  )
}
